// Catalog logic over the one-time-ported data (catalog.data.ts).
// Source of truth for: list prices, TARDOC line items, package composition,
// and the derived per-package category sums the insurance coverage calc needs.
// Ported from insurance-checker/proforma-generator (products.py + config.py +
// pdf/events.py + pdf/generator.py gender rules), 2026-06-17.
import { CATALOG, type CatalogLine } from "./catalog.data"
import { localizeProductLabel } from "./pdf/tardoc-labels"
import type { ProformaLang } from "./pdf/i18n"

export type TardocCategory =
  | "mri_scan"
  | "mri_999"
  | "consult_report"
  | "blood_test"
export type CategorySums = Record<TardocCategory, number>
export type Gender = "male" | "female"

const ZERO = (): CategorySums => ({
  mri_scan: 0,
  mri_999: 0,
  consult_report: 0,
  blood_test: 0,
})

// ── Categorisation (prefix table from PR #1606). The GM.00.0030 / GP.15 calls
// are the one open domain decision — change them here only. ───────────────────
export function categorizeLine(
  tarif: string,
  tarifziffer: string
): TardocCategory | null {
  if (
    tarif.startsWith("GP.00") ||
    tarif.startsWith("GP.10") ||
    tarif.startsWith("GP.15")
  )
    return "mri_scan"
  if (tarif === "GM.00" && tarifziffer === "0030") return "mri_scan"
  if (tarif === "AR.05" && tarifziffer === "0080") return "mri_scan"
  if (tarif.startsWith("GA.05")) return "mri_scan"
  if (tarif.startsWith("999")) return "mri_999"
  if (tarif.startsWith("AA.10")) return "consult_report"
  if (tarif.startsWith("AA.25")) return "consult_report"
  if (tarif.startsWith("317")) return "blood_test"
  return null
}

// ── Per-visit event expansion (pdf/events.py build_tardoc_events) ─────────
// Returns the FLAT multiset of service keys actually billed. Membership tiers
// repeat the follow-up service + report once per visit, so an N× membership
// bills N reports + N panels — which is what the proforma renders.
export function tardocServiceKeys(
  pkg: string,
  gender?: Gender,
  addons: string[] = []
): string[] {
  const def = CATALOG.PACKAGES[pkg]
  if (!def) throw new Error(`Unknown package: ${pkg}`)
  const set = new Set<string>(def.tardoc_services)
  for (const a of addons) {
    const meta = CATALOG.ADDONS[a]
    if (meta) set.add(meta.tardoc)
  }
  if (gender) {
    const r = CATALOG.GENDER_RULES[gender]
    if (r) {
      for (const [o, n] of Object.entries(r.tardoc_swap))
        if (set.has(o)) {
          set.delete(o)
          set.add(n)
        }
      for (const ex of r.exclude) set.delete(ex)
    }
  }

  const order = CATALOG.TARDOC_ORDER
  const followup = CATALOG.MEMBERSHIP_FOLLOWUP_COUNTS[pkg] ?? 0
  if (followup === 0) return order.filter((k) => set.has(k))

  const FU = CATALOG.FOLLOWUP_TARDOC_SERVICE
  const reportSuffix = set.has("additional_blood") ? ["additional_blood"] : []
  const baseline = order.filter(
    (k) => set.has(k) && k !== FU && k !== "additional_blood"
  )
  const keys = [...baseline, ...reportSuffix]
  for (let i = 0; i < followup; i++) keys.push(FU, ...reportSuffix)
  return keys
}

// ── Product labels + membership cover lines ──────────────────────────────

/** A priced product's label in the pro forma's language. The catalogue holds
 *  German and English; French comes from the same map the detail table uses. */
export function productPriceLabel(key: string, lang: ProformaLang): string {
  const p = CATALOG.PRODUCT_PRICES[key]
  if (!p) throw new Error(`Unknown product price key: ${key}`)
  if (lang === "en") return p.label_en
  return localizeProductLabel(p.label_de, lang)
}

/**
 * The cover lines a membership tier bills, for a document whose `package` is
 * something else.
 *
 * A pro forma bills ONE package. When a visitor buys a membership alongside a
 * check-up the package is the check-up, so the tier's own panels would never
 * appear: the customer paid CHF 499 their insurer never sees, and the estimate
 * on screen had to withhold that payout to stay honest. These lines are how
 * the tier reaches the document anyway.
 *
 * Priced from the catalogue at LIST, which is what a tier costs: a 2x is its
 * Advanced blood test (299) plus a follow-up (200), and `cover_products` keeps
 * the repeats, so a 4x bills three follow-ups and totals 899. Member pricing
 * never applies here — that is the discount a member GETS, not a discount on
 * their own subscription.
 *
 * Labelled by product, never "Ahead Membership": an insurer reimburses an
 * Advanced blood test, and a line naming the subscription is the one a
 * customer already had refused as proof of payment.
 */
export function membershipCoverLines(
  serviceId: string,
  lang: ProformaLang
): { description: string; amount: number }[] {
  // Membership ids only. Every check-up also has an `EMBED_SERVICES` entry,
  // so without this guard a Core MRI would hand back its own cover line and
  // the document would bill the check-up twice.
  if (!serviceId.startsWith("membership-")) return []
  const embed = EMBED_SERVICES[serviceId]
  if (!embed) return []
  const pkg = CATALOG.PACKAGES[embed.pkg]
  if (!pkg) return []
  return pkg.cover_products.map((key) => ({
    // `productPriceLabel` throws on a key the price table doesn't know, so a
    // cover product without a price surfaces instead of billing zero.
    description: productPriceLabel(key, lang),
    amount: CATALOG.PRODUCT_PRICES[key].price,
  }))
}

// ── Category sums for a key multiset ─────────────────────────────────────
export function categorySumsForKeys(keys: string[]): CategorySums {
  const sums = ZERO()
  for (const key of keys) {
    const svc = CATALOG.PRODUCT_SERVICES[key]
    if (!svc) continue
    for (const line of svc.lines) {
      const betrag = lineBetrag(line)
      if (betrag === null) continue
      const cat = categorizeLine(String(line[0]), String(line[1]))
      if (cat) sums[cat] += betrag
    }
  }
  for (const k of Object.keys(sums) as TardocCategory[])
    sums[k] = Math.round(sums[k] * 100) / 100
  return sums
}

function lineBetrag(line: CatalogLine): number | null {
  const v = line[10]
  return typeof v === "number" ? v : null
}

// ── Embed services → (package, gender) ───────────────────────────────────
export const EMBED_SERVICES: Record<string, { pkg: string; gender?: Gender }> =
  {
    "core-mri": { pkg: "core" },
    "advanced-mri": { pkg: "advanced" },
    "pro-mri": { pkg: "pro", gender: "female" }, // women's default (no PSA)
    "pro-mri-men": { pkg: "pro", gender: "male" },
    "blood-test": { pkg: "blood_only" },
    "membership-basic": { pkg: "membership_1x" },
    "membership-plus-1": { pkg: "membership_2x" },
    "membership-plus-3": { pkg: "membership_4x" },
  }

/** Per-category TARDOC sums for an embed service, derived from the catalog. */
export function embedCategorySums(embedServiceId: string): CategorySums {
  const e = EMBED_SERVICES[embedServiceId]
  if (!e) throw new Error(`Unknown embed service: ${embedServiceId}`)
  return categorySumsForKeys(tardocServiceKeys(e.pkg, e.gender))
}
